import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useMyEvents } from '@/hooks/useMyEvents';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Calendar, Users, Trophy, CreditCard, ArrowLeft, Home, Loader2, Clock, UserCheck, AlertCircle, LogIn, MapPin } from 'lucide-react';

interface ConfirmationStepProps {
  event: any;
  registrationData: any;
  onPrevious: () => void;
  paymentData?: any;
  isVerifyingPayment?: boolean;
  paymentFailed?: boolean;
}

export const ConfirmationStep = ({
  event,
  registrationData,
  onPrevious,
  paymentData,
  isVerifyingPayment = false,
  paymentFailed = false
}: ConfirmationStepProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { refetch: refetchMyEvents } = useMyEvents();
  const [isNavigating, setIsNavigating] = useState(false);
  
  // Detect if we came from Stripe payment via URL params
  const sessionIdFromUrl = searchParams.get('session_id');
  const paymentSuccessFromUrl = searchParams.get('payment_success') === 'true';

  // Setup auth state listener to react to session changes
  useEffect(() => {
    console.log('=== ConfirmationStep - Setting up auth listener ===');
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth state changed in ConfirmationStep:', event, session?.user?.email);
    });
    
    return () => subscription.unsubscribe();
  }, []);

  // Debug log on mount
  useEffect(() => {
    console.log('=== ConfirmationStep Mounted ===');
    console.log('Current user from AuthContext:', user?.id, user?.email);
    console.log('Props received:', {
      eventId: event?.id,
      eventTitle: event?.title,
      registrationData,
      paymentData,
      isVerifyingPayment
    });
    
    // NO limpiar localStorage aquí - lo haremos al navegar a "Mis Eventos"
  }, [event, registrationData, paymentData, isVerifyingPayment, user]);

  // Refetch "Mis Eventos" after successful payment verification
  useEffect(() => {
    if (!isVerifyingPayment && paymentData?.paymentStatus === 'paid') {
      console.log('✅ Payment verified as paid, refetching Mis Eventos immediately...');
      refetchMyEvents();
      console.log('✅ Mis Eventos refetch triggered');
    }
  }, [isVerifyingPayment, paymentData, refetchMyEvents]);

  // PRIORIDAD 1: Mostrar loading mientras se verifica el pago
  if (isVerifyingPayment) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <h2 className="text-xl font-semibold">Verificando pago...</h2>
        <p className="text-gray-600">Por favor espera mientras confirmamos tu pago.</p>
      </div>
    );
  }

  // PRIORIDAD 1.5: Mostrar error si el pago falló
  if (paymentFailed) {
    const handleRetryPayment = () => {
      onPrevious();
    };

    const handleGoHomeAfterError = () => {
      localStorage.removeItem('pendingRegistrationData');
      navigate('/', { replace: true });
    };

    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <AlertCircle className="h-10 w-10 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error en el Pago</h2>
          <p className="text-gray-600">
            No pudimos verificar tu pago. Esto puede ocurrir si el pago fue rechazado o si hubo un problema con la conexión.
          </p>
        </div>

        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="text-center">
                <CreditCard className="h-12 w-12 text-red-400 mx-auto mb-2" />
                <h3 className="text-lg font-semibold text-red-900">
                  {event?.title || 'Evento'}
                </h3>
                <p className="text-sm text-red-700 mt-2">
                  El pago no fue procesado correctamente
                </p>
              </div>

              <div className="border-t border-red-200 pt-4 space-y-2 text-sm text-red-800">
                <p>Posibles causas:</p>
                <ul className="list-disc list-inside space-y-1 text-red-700">
                  <li>La tarjeta fue rechazada</li>
                  <li>Fondos insuficientes</li>
                  <li>El pago fue cancelado</li>
                  <li>Problema de conexión durante el proceso</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div className="text-sm text-primary">
                <p className="font-medium mb-1">No se realizó ningún cargo</p>
                <p className="text-primary/80">
                  Si el pago fue rechazado, no se ha cobrado nada a tu tarjeta. Puedes intentar de nuevo con otra tarjeta o método de pago.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-col gap-3 pt-4">
          <Button
            onClick={handleRetryPayment}
            className="w-full bg-primary hover:bg-primary/90"
            size="lg"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Intentar de Nuevo
          </Button>
          <Button
            onClick={handleGoHomeAfterError}
            variant="outline"
            className="w-full"
          >
            <Home className="h-4 w-4 mr-2" />
            Volver al Inicio
          </Button>
        </div>
      </div>
    );
  }

  // PRIORIDAD 2: Si venimos de Stripe con session_id y payment_success
  // Verificar si hay usuario activo o no
  if (sessionIdFromUrl && paymentSuccessFromUrl && !paymentData) {
    const hasNoActiveUser = !user;
    
    // Caso A: SIN usuario activo - mostrar resumen + solicitar login
    if (hasNoActiveUser) {
      const handleLoginToViewEvents = () => {
        localStorage.removeItem('pendingRegistrationData');
        navigate('/auth', { 
          replace: true, 
          state: { 
            redirectTo: '/',
            activeTab: 'my-events',
            message: 'Inicia sesión para ver tus eventos inscritos'
          } 
        });
      };

      const eventDate = event?.start_date 
        ? new Date(event.start_date).toLocaleDateString('es-ES', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          })
        : 'Por confirmar';

      return (
        <div className="space-y-6">
          {/* Icono de éxito */}
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">¡Pago Completado!</h2>
            <p className="text-gray-600">
              Tu inscripción ha sido procesada exitosamente.
            </p>
          </div>

          {/* Resumen del evento */}
          <Card className="border-green-200 bg-green-50">
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div className="text-center">
                  <Trophy className="h-12 w-12 text-green-600 mx-auto mb-2" />
                  <h3 className="text-lg font-semibold text-green-900">
                    {event?.title || 'Evento'}
                  </h3>
                  {event?.sport && (
                    <p className="text-sm text-green-700 mt-1">
                      {event.sport}
                    </p>
                  )}
                </div>
                
                {/* Detalles de la inscripción */}
                <div className="border-t border-green-200 pt-4 space-y-3">
                  {event?.start_date && (
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="h-4 w-4 text-green-600" />
                      <span className="text-green-800 font-medium">Fecha:</span>
                      <span className="text-green-700">{eventDate}</span>
                    </div>
                  )}
                  
                  {event?.location && (
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="h-4 w-4 text-green-600" />
                      <span className="text-green-800 font-medium">Ubicación:</span>
                      <span className="text-green-700">{event.location}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between text-sm pt-2">
                    <span className="text-green-800 font-medium">Estado del Pago:</span>
                    <Badge className="bg-green-600 text-white">Completado</Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Mensaje informativo */}
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div className="text-sm text-primary">
                  <p className="font-medium mb-1">Inicia sesión para continuar</p>
                  <p className="text-primary/80">
                    Para ver los detalles completos de tu inscripción en "Mis Eventos", 
                    necesitas iniciar sesión con tu cuenta.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Botón principal de login */}
          <div className="flex flex-col gap-3 pt-4">
            <Button 
              onClick={handleLoginToViewEvents} 
              className="w-full bg-primary hover:bg-primary/90"
              size="lg"
            >
              <LogIn className="h-5 w-5 mr-2" />
              Iniciar Sesión para Ver Mis Eventos
            </Button>
          </div>
          
          <p className="text-sm text-gray-500 text-center">
            Tu inscripción ha sido guardada. Después de iniciar sesión, 
            la verás en tu panel de "Mis Eventos".
          </p>
        </div>
      );
    }
    
    // Caso B: CON usuario activo - mostrar botón normal de "Ver Mis Eventos"
    const handleGoHomeFromStripe = () => {
      localStorage.removeItem('pendingRegistrationData');
      navigate('/', { replace: true, state: { activeTab: 'my-events', forceRefresh: true } });
    };

    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">¡Pago Completado!</h2>
          <p className="text-gray-600">
            Tu inscripción ha sido procesada exitosamente.
          </p>
        </div>

        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6 text-center">
            <Trophy className="h-12 w-12 text-green-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-green-900">{event?.title || 'Evento'}</h3>
            <p className="text-sm text-green-700 mt-2">
              Los detalles de tu inscripción aparecerán en "Mis Eventos"
            </p>
          </CardContent>
        </Card>

        <div className="flex flex-col gap-3 pt-4">
          <Button 
            onClick={handleGoHomeFromStripe}
            className="w-full bg-green-600 hover:bg-green-700"
          >
            <Home className="h-4 w-4 mr-2" />
            Ver Mis Eventos
          </Button>
        </div>
        
        <p className="text-sm text-gray-500 text-center">
          Tu inscripción ha sido guardada. Puedes ver los detalles en tu panel de eventos.
        </p>
      </div>
    );
  }

  // PRIORIDAD 3: Fallback para cualquier session_id (con o sin payment_success)
  // Solo mostrar si NO hay fallo de pago (paymentFailed se maneja arriba)
  if (sessionIdFromUrl && !paymentData && !paymentFailed) {
    // También verificar si hay usuario activo
    if (!user) {
      const handleLoginFallback = () => {
        localStorage.removeItem('pendingRegistrationData');
        navigate('/auth', { 
          replace: true, 
          state: { 
            redirectTo: '/',
            activeTab: 'my-events',
            message: 'Inicia sesión para ver tus eventos inscritos'
          } 
        });
      };

      return (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">¡Inscripción Procesada!</h2>
            <p className="text-gray-600">Tu pago ha sido recibido.</p>
          </div>

          <Card className="border-green-200 bg-green-50">
            <CardContent className="pt-6 text-center">
              <Trophy className="h-12 w-12 text-green-600 mx-auto mb-2" />
              <h3 className="text-lg font-semibold text-green-900">{event?.title || 'Evento'}</h3>
            </CardContent>
          </Card>

          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div className="text-sm text-primary">
                  <p className="font-medium mb-1">Inicia sesión para continuar</p>
                  <p className="text-primary/80">
                    Para ver los detalles en "Mis Eventos", necesitas iniciar sesión.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-col gap-3 pt-4">
            <Button 
              onClick={handleLoginFallback} 
              className="w-full bg-primary hover:bg-primary/90"
              size="lg"
            >
              <LogIn className="h-5 w-5 mr-2" />
              Iniciar Sesión para Ver Mis Eventos
            </Button>
          </div>
        </div>
      );
    }

    const handleGoHomeFallback = () => {
      localStorage.removeItem('pendingRegistrationData');
      navigate('/', { replace: true, state: { activeTab: 'my-events', forceRefresh: true } });
    };
    
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">¡Inscripción Procesada!</h2>
          <p className="text-gray-600">
            Tu pago ha sido recibido.
          </p>
        </div>

        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <Trophy className="h-12 w-12 text-green-600 mx-auto" />
              <div>
                <h3 className="text-lg font-semibold text-green-900">{event?.title || 'Evento'}</h3>
                <p className="text-sm text-green-700">Revisa "Mis Eventos" para ver los detalles</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-col gap-3 pt-4">
          <Button 
            onClick={handleGoHomeFallback} 
            className="w-full bg-green-600 hover:bg-green-700"
          >
            <Home className="h-4 w-4 mr-2" />
            Ver Mis Eventos
          </Button>
        </div>
      </div>
    );
  }

  // Determine the actual registration data source  
  // If we have payment data, prioritize metadata from Stripe
  let actualRegistrationData;
  if (paymentData?.metadata) {
    console.log('ConfirmationStep - Using Stripe metadata:', paymentData.metadata);
    // Data comes from successful payment - use metadata from Stripe
    // Prioritize partner_full_name over player2_name for friend selections
    const partnerName = paymentData.metadata.partner_full_name || paymentData.metadata.player2_name;
    
    actualRegistrationData = {
      modality: paymentData.metadata.modality,
      category: paymentData.metadata.category,
      subcategory: paymentData.metadata.subcategory,
      player1Name: paymentData.metadata.player1_name,
      player2Name: partnerName,
      partnerSelectionMethod: paymentData.metadata.partner_selection_method,
      paymentMode: paymentData.metadata.payment_mode,
      partner_user_id: paymentData.metadata.partner_user_id,
      partner_full_name: paymentData.metadata.partner_full_name
    };
  } else {
    console.log('ConfirmationStep - Using registrationData:', registrationData);
    // Fallback to original registration data
    actualRegistrationData = registrationData;
  }
  
  console.log('ConfirmationStep - actualRegistrationData:', actualRegistrationData);
  
  // Determine if a real payment was made
  const isRealPayment = paymentData && paymentData.paymentStatus === 'paid';
  
  // Get the actual amount paid from Stripe or calculate it
  const actualAmountPaid = paymentData?.amountTotal ? paymentData.amountTotal / 100 : 0; // Convert from cents
  
  // Determine waiting status for partner - check multiple possible sources
  const paymentModeFromData = actualRegistrationData?.paymentMode || 
                              actualRegistrationData?.payment_mode || 
                              paymentData?.metadata?.payment_mode;
  
  const isWaitingForPartner = actualRegistrationData?.modality === 'dobles' && 
                             actualRegistrationData?.partnerSelectionMethod === 'friend' && 
                             (paymentModeFromData === 'partial' || paymentModeFromData === 'my_part');

  // Helper function to get display values with proper priority
  const getDisplayData = () => {
    console.log('=== getDisplayData Debug ===');
    console.log('paymentData:', paymentData);
    console.log('paymentData.metadata:', paymentData?.metadata);
    console.log('registrationData:', registrationData);

    // PRIORIDAD 1: Datos de Stripe metadata (fuente de verdad después del pago)
    if (paymentData?.metadata?.category && paymentData.metadata.category !== 'No especificada') {
      console.log('✅ Using paymentData.metadata from Stripe (Priority 1 - Source of Truth)');
      return {
        category: paymentData.metadata.category,
        subcategory: paymentData.metadata.subcategory || null,
        modality: paymentData.metadata.modality || 'singles',
        player1Name: paymentData.metadata.player1_name || user?.email || 'Jugador 1',
        player2Name: paymentData.metadata.partner_full_name || 
                     paymentData.metadata.player2_name || 
                     'Compañero por definir',
        partnerMethod: paymentData.metadata.partner_selection_method || 'manual',
        paymentMode: paymentData.metadata.payment_mode || 'full_amount',
        selectedFriend: paymentData.metadata.partner_full_name ? {
          full_name: paymentData.metadata.partner_full_name,
          email: paymentData.metadata.partner_email || ''
        } : null
      };
    }

    // PRIORIDAD 2: Datos de registrationData prop
    if (registrationData?.category && registrationData.category !== 'No especificada') {
      console.log('✅ Using registrationData from prop (Priority 2)');
      const modality = registrationData.modality || 'singles';
      let player2Name = 'Compañero por definir';
      
      if (modality === 'dobles') {
        if (registrationData.selectedFriend?.full_name) {
          player2Name = registrationData.selectedFriend.full_name;
        } else if (registrationData.player2Name) {
          player2Name = registrationData.player2Name;
        }
      }

      return {
        category: registrationData.category,
        subcategory: registrationData.subcategory || null,
        modality: modality,
        player1Name: registrationData.player1Name || user?.email || 'Jugador 1',
        player2Name: player2Name,
        partnerMethod: registrationData.partnerSelectionMethod || 'manual',
        paymentMode: registrationData.paymentMode || 'full_amount',
        selectedFriend: registrationData.selectedFriend || null
      };
    }
    
    // PRIORIDAD 3: Leer directamente de localStorage (fallback adicional)
    const pendingData = localStorage.getItem('pendingRegistrationData');
    if (pendingData) {
      try {
        const parsedData = JSON.parse(pendingData);
        if (parsedData.category && parsedData.category !== 'No especificada') {
          console.log('✅ Using localStorage directly (Priority 3)');
          const modality = parsedData.modality || 'singles';
          let player2Name = 'Compañero por definir';
          
          if (modality === 'dobles') {
            if (parsedData.selectedFriend?.full_name) {
              player2Name = parsedData.selectedFriend.full_name;
            } else if (parsedData.player2Name) {
              player2Name = parsedData.player2Name;
            }
          }

          return {
            category: parsedData.category,
            subcategory: parsedData.subcategory || null,
            modality: modality,
            player1Name: parsedData.player1Name || user?.email || 'Jugador 1',
            player2Name: player2Name,
            partnerMethod: parsedData.partnerSelectionMethod || 'manual',
            paymentMode: parsedData.paymentMode || 'full_amount',
            selectedFriend: parsedData.selectedFriend || null
          };
        }
      } catch (e) {
        console.error('❌ Error parsing localStorage:', e);
      }
    }

    // PRIORIDAD 4: Valores por defecto (solo si todo falla)
    console.log('⚠️ Using default values - NO DATA FOUND (Priority 4)');
    return {
      category: 'No especificada',
      subcategory: null,
      modality: 'singles',
      player1Name: user?.email || 'Jugador 1',
      player2Name: 'Compañero por definir',
      partnerMethod: 'manual',
      paymentMode: 'full_amount',
      selectedFriend: null
    };
  };


  const displayData = getDisplayData();

  const handleGoHome = async () => {
    console.log('=== handleGoHome - Waiting for Session ===');
    setIsNavigating(true);
    
    // Dar tiempo para que Supabase rehidrate la sesión desde localStorage
    // Especialmente importante cuando se vuelve de una nueva pestaña (Stripe)
    let session = null;
    let attempts = 0;
    const maxAttempts = 5;
    
    while (!session && attempts < maxAttempts) {
      const { data } = await supabase.auth.getSession();
      session = data.session;
      
      if (!session) {
        console.log(`Attempt ${attempts + 1}/${maxAttempts}: Session not ready, waiting...`);
        await new Promise(resolve => setTimeout(resolve, 500)); // Esperar 500ms
        attempts++;
      }
    }
    
    console.log('Session after waiting:', {
      id: session?.user?.id,
      email: session?.user?.email,
      attempts: attempts
    });
    
    // Si después de esperar aún no hay sesión, usar el user del AuthContext como fallback
    if (!session?.user) {
      if (user) {
        console.log('✅ Fallback: Using user from AuthContext:', user.email);
        navigate('/', { 
          replace: true, 
          state: { activeTab: 'my-events', forceRefresh: true } 
        });
        return;
      }
      
      console.warn('❌ No session found after retries and no user in context. Redirecting to auth.');
      setIsNavigating(false);
      navigate('/auth');
      return;
    }
    
    // Verificar que el usuario actual coincide con el esperado
    console.log('✅ Navigating to Mis Eventos for user:', session.user.email);
    
    navigate('/', { 
      replace: true, 
      state: { activeTab: 'my-events', forceRefresh: true } 
    });
  };

  const handleViewEvent = () => {
    if (event.event_type === 'league') {
      navigate(`/league/${event.id}`);
    } else {
      navigate(`/tournament/${event.id}`);
    }
  };

  return (
    <div className="space-y-6">
      {/* Success Header */}
      <div className="text-center mb-8">
        <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
          <CheckCircle className="h-10 w-10 text-green-600" />
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">¡Inscripción Completada!</h2>
        <p className="text-gray-600 text-lg">
          Te has inscrito exitosamente al {event.event_type === 'league' ? 'liga' : 'torneo'}
        </p>
      </div>

      {/* Registration Details */}
      <Card className="border-green-200 bg-green-50">
        <CardHeader>
          <CardTitle className="text-xl text-green-800 flex items-center gap-2">
            <Trophy className="h-6 w-6" />
            Detalles de tu Inscripción
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Event Information */}
          <div className="bg-white rounded-lg p-4 border border-green-200">
            <h4 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
              <Trophy className="h-4 w-4" />
              Información del Evento
            </h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div>
                  <span className="text-sm font-medium text-green-700 block">Evento</span>
                  <span className="text-green-900 font-semibold">{event.title || event.name}</span>
                </div>
                
                <div>
                  <span className="text-sm font-medium text-green-700 block">Deporte</span>
                  <Badge variant="outline" className="bg-white border-green-300 text-green-800">
                    {event.sport}
                  </Badge>
                </div>
              </div>

              <div className="space-y-2">
                <div>
                  <span className="text-sm font-medium text-green-700 block">Modalidad</span>
                  <Badge variant="outline" className="bg-white border-green-300 text-green-800">
                    {displayData.modality === 'singles' ? 'Singles (Individual)' : 'Dobles (Parejas)'}
                  </Badge>
                </div>

                <div>
                  <span className="text-sm font-medium text-green-700 block">Tipo</span>
                  <Badge variant="outline" className="bg-white border-green-300 text-green-800">
                    {event.event_type === 'league' ? 'Liga' : 'Torneo'}
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          {/* Registration Details */}
          <div className="bg-white rounded-lg p-4 border border-green-200">
            <h4 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
              <UserCheck className="h-4 w-4" />
              Detalles de tu Inscripción
            </h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-green-700 block">Categoría</span>
                  <span className="text-green-900 font-semibold">{displayData.category}</span>
                </div>
                
                {displayData.subcategory && (
                  <div>
                    <span className="text-sm font-medium text-green-700 block">Subcategoría</span>
                    <span className="text-green-900 font-semibold">{displayData.subcategory}</span>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-green-700 block">Participante(s)</span>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                      <span className="text-green-900 font-semibold">{displayData.player1Name}</span>
                    </div>
                    
                    {displayData.modality === 'dobles' && (
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                        <span className="text-green-900 font-semibold">
                          {displayData.player2Name}
                        </span>
                        {displayData.partnerMethod === 'friend' && displayData.selectedFriend && (
                          <Badge variant="outline" className="text-xs bg-primary/5 border-primary/30 text-primary">
                            Amigo
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Information */}
          <div className="bg-white rounded-lg p-4 border border-green-200">
            <h4 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Información de Pago
            </h4>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-green-700 font-medium">Monto Pagado:</span>
                <span className="text-2xl font-bold text-green-800">
                  €{isRealPayment ? actualAmountPaid.toFixed(2) : 
                    (actualRegistrationData?.payment_amount ? 
                      (actualRegistrationData.payment_amount / 100).toFixed(2) : 
                      '0.00'
                    )
                  }
                </span>
              </div>

              {isWaitingForPartner ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="h-4 w-4 text-yellow-600" />
                    <span className="font-medium text-yellow-800">Esperando Pago de Compañero</span>
                  </div>
                  <div className="text-sm text-yellow-700 space-y-1">
                    <p>• Has pagado tu parte (€{isRealPayment ? actualAmountPaid : (actualRegistrationData?.payment_amount ? (actualRegistrationData.payment_amount / 100) : 0)}) del total (€{event.entry_fee})</p>
                    <p>• Tu compañero {actualRegistrationData?.selectedFriend?.full_name || actualRegistrationData?.selectedFriend?.email} debe pagar €{event.entry_fee / 2}</p>
                    <p>• Se le enviará una invitación para completar su pago</p>
                  </div>
                </div>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="font-medium text-green-800">
                      {actualRegistrationData?.modality === 'dobles' ? 'Pago Completo por Ambos Participantes' : 'Pago Completo'}
                    </span>
                  </div>
                  <div className="text-sm text-green-700">
                    {actualRegistrationData?.modality === 'dobles' 
                      ? `Has pagado el monto completo (€${isRealPayment ? actualAmountPaid : (actualRegistrationData?.payment_amount ? (actualRegistrationData.payment_amount / 100) : 0)}) por tu inscripción y la de tu compañero`
                      : `Has pagado el monto completo (€${isRealPayment ? actualAmountPaid : (actualRegistrationData?.payment_amount ? (actualRegistrationData.payment_amount / 100) : 0)}) por tu inscripción`
                    }
                  </div>
                </div>
              )}

              {isRealPayment && (
                <div className="mt-3 text-sm text-green-600 bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle className="h-4 w-4" />
                    <span className="font-medium">Pago procesado exitosamente por Stripe</span>
                  </div>
                  {paymentData.customerEmail && (
                    <div className="text-green-700">
                      Confirmación enviada a: {paymentData.customerEmail}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Next Steps */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Próximos Pasos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
              <div>
                <p className="font-medium">Confirmación por Email</p>
                <p className="text-gray-600">
                  Recibirás un email de confirmación con todos los detalles de tu inscripción.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
              <div>
                <p className="font-medium">Información del Evento</p>
                <p className="text-gray-600">
                  Te notificaremos sobre horarios, ubicación y cualquier información adicional.
                </p>
              </div>
            </div>

            {isWaitingForPartner && (
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-orange-600 rounded-full mt-2"></div>
                <div>
                  <p className="font-medium">Notificación a tu Compañero</p>
                  <p className="text-gray-600">
                    Se enviará una invitación de pago a tu compañero para completar la inscripción.
                  </p>
                </div>
              </div>
            )}

            {event.event_type === 'league' && (
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                <div>
                  <p className="font-medium">Calendario de Partidos</p>
                  <p className="text-gray-600">
                    Podrás ver el calendario de partidos y coordinar con otros participantes.
                  </p>
                </div>
              </div>
            )}

            {event.event_type === 'tournament' && (
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                <div>
                  <p className="font-medium">Bracket del Torneo</p>
                  <p className="text-gray-600">
                    Una vez que se complete la inscripción, podrás ver el bracket y los emparejamientos.
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Action Button */}
      <div className="flex justify-center pt-6 border-t">
        <Button
          onClick={handleGoHome}
          disabled={isNavigating}
          className="w-full sm:w-auto px-8 bg-primary hover:bg-primary/90 text-primary-foreground flex items-center justify-center gap-2"
        >
          {isNavigating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Verificando sesión...
            </>
          ) : (
            <>
              <Home className="h-4 w-4" />
              Ver Mis Eventos
            </>
          )}
        </Button>
      </div>
    </div>
  );
};